import type { BillingActivationFailure, Entitlements } from '@tandemone/types'
import { ACTIVATION_RETRY_LABELS, PLAN_CATALOG } from '@tandemone/types'
import { AlertTriangle } from 'lucide-react'
import { useLocation } from 'react-router'

import { NoticeBanner } from './notice-banner'

const WHY: Record<BillingActivationFailure, string> = {
  no_payment_method: 'we had no card on file',
  card_declined: 'your card was declined',
  stripe_error: 'something went wrong on our side'
}

/* keyed on the failure timestamp, not the user alone: a second failure is a new
 * problem and has to come back after the first was dismissed. */
export function ActivationFailedBanner({
  entitlements,
  userId
}: {
  entitlements: Entitlements
  userId: string | null
}) {
  const onBilling = useLocation().pathname.startsWith('/billing')
  const reason = entitlements.activationFailure
  if (!reason) return null
  const failedPlan = entitlements.activationFailedPlanId
  const planLabel = failedPlan ? PLAN_CATALOG[failedPlan].label : null

  return (
    <NoticeBanner
      tone='warning'
      icon={AlertTriangle}
      /* on billing the retry lives on the failed plan's own column, where it
       * says which plan it restarts. two of them would just compete. */
      action={
        onBilling
          ? undefined
          : { label: ACTIVATION_RETRY_LABELS[reason], to: '/billing' }
      }
      dismissId={
        onBilling
          ? undefined
          : `activation-failed:${userId ?? 'anon'}:${entitlements.activationFailedAt ?? 'none'}`
      }
    >
      {planLabel
        ? `Your ${planLabel} plan didn’t start`
        : 'Your plan didn’t start'}{' '}
      because {WHY[reason]}. You&rsquo;re on {entitlements.planLabel} and
      nothing was charged. Choose your plan again to start it.
    </NoticeBanner>
  )
}
